import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Menu, X, Expand } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function CategoryNav({ configz = {} }) {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // ====================== DEFAULT CONFIG ======================
  const defaultConfig = {
    preset: "elegant-pastel",
   categories: [
  {
    name: "Whisky",
    icon: null,
    submenu: [
      "Jameson",
      "Johnie Walker Black Label",
      "Johnie Walker Red Label",
      "Johnie Walker Green Label",
      "Johnie Walker Gold Label",
      "Jack Daniels",
      "Chivas",
      "Singleton",
      "Vat 69"
    ],
    featured: true
  },
  {
    name: "Gin",
    icon: null,
    submenu: [
      "Gilbey’s Special Dry Gin",
      "Gordon’s London Dry Gin",
      "Gordon’s Premium Pink",
      "Gordon’s Mediterranean Orange",
      "Beefeater London Dry",
      "Tanqueray London Dry"
    ],
    featured: false
  },
  {
    name: "Vodka",
    icon: null,
    submenu: [
      "Smirnoff Red Label",
      "Kibao Vodka",
      "Chrome Vodka",
      "Cîroc",
      "Absolute Vodka"
    ],
    featured: false
  },
  {
    name: "Brandy & Cognac",
    icon: null,
    submenu: [
      "Hennessy V.S",
      "Viceroy Brandy",
      "Richot Brandy",
      "Rémy Martin V.S.O.P",
      "Martell V.S.O.P",
      "Courvoisier V.S",
      "Martell Blue Swift"
    ],
    featured: false
  },
  {
    name: "Wine",
    icon: "⭐",
    submenu: [
      "Robertson Winery Sweet Red",
      "4th Street Sweet Red",
      "Four Cousins",
      "Frontera Cabernet Sauvignon"
    ],
    featured: false
  },
  {
    name: "Beers & Ciders",
    icon: "⭐",
    submenu: [
      "Tusker Lager",
      "White Cap Crisp",
      "Tusker Cider",
      "Guinness",
      "Heineken",
      "Savanna Dry Cider",
      "Tusker Malt",
      "Balozi Lager",
      "White Cap Lager"
    ],
    featured: false
  },
  {
    name: "Liqueurs & Tequila",
    icon: "⭐",
    submenu: [
      "Baileys Irish Cream",
      "Jägermeister",
      "Don Julio",
      "Amarula Cream",
      "Campari"
    ],
    featured: false
  }
],
    devMode: true,
    customStyles: {
      backgroundColor: "bg-white",
      textColor: "#ffffff",
      hoverBg: "hover:bg-purple-50",
      hoverText: "hover:text-purple-700",
      featuredBg: "from-orange-600 to-pink-600"
    }
  };

  // ====================== PARSE CONFIG ======================
  const parsedConfig = useMemo(() => {
    if (typeof configz === 'string') {
      try {
        const parsed = JSON.parse(configz);
        //console.log("✅ Parsed from JSON string successfully");
        return parsed;
      } catch (e) {
        //console.error("Failed to parse configz as JSON:", e);
        return defaultConfig; // Fallback to default
      }
    }

    // If configz is an object and has categories, use it
    if (configz && typeof configz === 'object' && configz.categories?.length > 0) {
      return configz;
    }

    // Otherwise use defaultConfig
    return defaultConfig;
  }, [configz]);

  // Extract data from parsed config
  const categories = parsedConfig.categories || [];
  const customStyles = parsedConfig.customStyles || {};

  // ====================== STYLES ======================
  const styles = {
    navBg: customStyles.backgroundColor || 'bg-white',
    navText: customStyles.textColor || 'text-gray-700',
    hoverBg: customStyles.hoverBg || 'hover:bg-gray-50',
    hoverText: customStyles.hoverText || 'hover:text-purple-700',
    activeBg: customStyles.featuredBg || 'bg-purple-50',
    activeText: customStyles.hoverText || 'text-purple-700',
    featuredBg: customStyles.featuredBg || 'from-purple-600 to-pink-600',
    featuredText: 'text-white',
    submenuBg: customStyles.submenuBg || 'bg-white',
    submenuBorder: customStyles.submenuBorder || 'border-gray-100',
    submenuShadow: customStyles.submenuShadow || 'shadow-xl',
    mobileBg: customStyles.mobileBg || 'bg-white',
    mobileHeaderText: customStyles.mobileHeaderText ||
      'bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent',
    borderColor: customStyles.borderColor || 'border-gray-100',
  };

  // ====================== DEBUG ======================
 

  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };

    if (activeMenu !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeMenu]);

  const handleNavigate = (slug) => {
    navigate(`/Shop/all/${slug.toLowerCase()}`);
    setActiveMenu(null);
    setMobileOpen(false);
  };

  const toggleMenu = (idx) => {
    setActiveMenu(activeMenu === idx ? null : idx);
  };

  return (
    <>
      {/* Desktop Navigation */}
      <nav
        ref={dropdownRef}
        className={`hidden lg:block ${styles.navBg} transition-all duration-300 relative left-0 right-0 z-30`}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-1 h-14">
            {categories.map((cat, idx) => (
              <div key={idx} className="relative">
                <button
                  onClick={() => {
                    if (cat.submenu?.length > 0) {
                      toggleMenu(idx);
                    } else {
                      handleNavigate(cat.name);
                    }
                  }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg transition-all duration-200 text-lg font-medium ${
                    cat.featured
                      ? `bg-gradient-to-r ${styles.featuredBg} ${styles.featuredText} hover:shadow-md`
                      : activeMenu === idx
                      ? `${styles.activeBg} ${styles.activeText}`
                      : `${styles.navText} ${styles.hoverBg} ${styles.hoverText}`
                  }`}
                >
                  <span className="whitespace-nowrap">{cat.name}</span>
                  {cat.submenu?.length > 0 && (
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${
                        activeMenu === idx ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </button>

                {cat.submenu?.length > 0 && (
                  <div
                    className={`absolute top-full left-0 mt-1 w-56 ${styles.submenuBg} rounded-lg border ${styles.submenuBorder} ${styles.submenuShadow} overflow-hidden transition-all duration-200 ${
                      activeMenu === idx
                        ? 'opacity-100 translate-y-0 visible'
                        : 'opacity-0 -translate-y-2 invisible pointer-events-none'
                    }`}
                  >
                    <div className="py-2">
                      {cat.submenu.map((item, i) => (
                        <button
                          key={i}
                          onClick={() => handleNavigate(item)}
                          className={`w-full text-left px-4 py-2.5 text-lg ${styles.navText} ${styles.hoverBg} ${styles.hoverText} transition-all duration-200 hover:pl-5`}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className={`md:hidden fixed w-full z-30 border-b ${styles.borderColor} ${styles.mobileBg}`}>
        <div className="px-4 py-3 mt-3 flex items-center justify-between">
          <span className={`text-base font-bold ${styles.mobileHeaderText}`}>
            Discover
          </span>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`p-2 rounded-lg ${styles.hoverBg} transition-colors`}
          >
            {mobileOpen ? (
              <X className="w-5 h-5 text-gray-700" />
            ) : (
              <Expand className="w-5 h-5 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Overlay */}
        <div
          className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
            mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setMobileOpen(false)}
        />

        {/* Mobile Menu */}
        <div
          className={`fixed top-[17.2%] md:top-[15%] left-0 right-0 bottom-0 ${styles.mobileBg} transform transition-transform duration-300 overflow-y-auto ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="px-4 py-3 flex items-center justify-between">
            <span className={`text-base font-bold ${styles.mobileHeaderText}`}>
              Discover
            </span>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`p-2 rounded-lg ${styles.hoverBg} transition-colors`}
            >
              {mobileOpen ? (
                <X className="w-5 h-5 text-gray-700" />
              ) : (
                <Menu className="w-5 h-5 text-gray-700" />
              )}
            </button>
          </div>

          <div className="p-4 space-y-1">
            {categories.map((cat, idx) => (
              <div key={idx} className="border-b border-gray-100 last:border-0 pb-2 last:pb-0">
                <button
                  onClick={() => {
                    if (cat.submenu?.length > 0) {
                      setActiveMenu(activeMenu === idx ? null : idx);
                    } else {
                      handleNavigate(cat.name);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 ${
                    cat.featured
                      ? `bg-gradient-to-r ${styles.featuredBg} ${styles.featuredText}`
                      : activeMenu === idx
                      ? `${styles.activeBg} ${styles.activeText}`
                      : `${styles.hoverBg}`
                  }`}
                >
                  <span className={`font-semibold text-lg ${cat.featured ? 'text-white' : ''}`}>
                    {cat.name}
                  </span>
                  {cat.submenu?.length > 0 && (
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${
                        activeMenu === idx ? 'rotate-180' : ''
                      } ${cat.featured ? 'text-white' : 'text-gray-500'}`}
                    />
                  )}
                </button>

                {cat.submenu?.length > 0 && activeMenu === idx && (
                  <div className="overflow-hidden transition-all duration-300 max-h-96 mt-1">
                    <div className="space-y-1">
                      {cat.submenu.map((item, i) => (
                        <button
                          key={i}
                          onClick={() => handleNavigate(item)}
                          className={`w-full text-left px-4 py-2.5 ml-4 text-lg ${styles.navText} ${styles.hoverBg} ${styles.hoverText} rounded-lg transition-all duration-200`}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
}